import { flow, types, SnapshotIn } from 'mobx-state-tree';
import { listOcrDocuments, uploadOcrPdf, createAudiobookFromOcr, deleteOcrDocument } from 'services/csAudiobookApi';
import { withRootStore } from '../withRootStore';
import { OCR_MAX_PDF_BYTES } from 'config/constants';

export const OcrDocumentModel = types.model('OcrDocument', {
  id: types.identifier,
  filename: types.string,
  size: types.maybeNull(types.number),
  document_type: types.maybeNull(types.string),
  created_at: types.maybeNull(types.string),
  updated_at: types.maybeNull(types.string),
  page_count: types.maybeNull(types.number),
  organization_id: types.maybeNull(types.string),
});

type OcrDocSnapshot = SnapshotIn<typeof OcrDocumentModel>;

export const OcrStore = types
  .model('OcrStore', {
    items: types.optional(types.array(OcrDocumentModel), []),
    isLoading: false,
    uploadProgress: types.maybeNull(types.number),
    isUploading: false,
    lastFetchedAt: types.maybeNull(types.number),
    fetchedLimit: types.maybeNull(types.number),
    nextCursorUpdatedAt: types.maybeNull(types.string),
    nextCursorId: types.maybeNull(types.string),
  })
  .extend(withRootStore)
  .views((self) => ({
    get itemsArray() {
      return self.items.slice();
    },
    get hasFullList() {
      return (self.fetchedLimit ?? 0) >= 200 || self.items.length >= 200;
    },
    get hasNextPage() {
      return !!self.nextCursorId && !!self.nextCursorUpdatedAt;
    },
  }))
  .actions((self) => {
    const hydrateFromCache = () => {
      try {
        const raw = sessionStorage.getItem('ocr_docs_cache');
        if (!raw) return;
        const arr = JSON.parse(raw) as OcrDocSnapshot[];
        if (!Array.isArray(arr) || arr.length === 0) return;
        if (self.items.length > 0) return;
        const models = arr.map((snap) =>
          OcrDocumentModel.create({
            id: String(snap.id ?? ''),
            filename: (snap.filename as string) ?? '',
            size: (snap.size as number) ?? null,
            document_type: (snap.document_type as string) ?? null,
            created_at: (snap.created_at as string) ?? null,
            updated_at: (snap.updated_at as string) ?? null,
            page_count: (snap.page_count as number) ?? null,
            organization_id: (snap.organization_id as string) ?? null,
          })
        );
        self.items.replace(models);
      } catch (_) {
        // ignore cache errors
      }
    };

    const list = flow(function* ({
      documentType = 'audiobook',
      organizationId,
      limit = 50,
      suppressError = false,
      background = false,
      cursorUpdatedAt,
      cursorId,
      append = false,
      noCache = false,
    }: {
      documentType?: string;
      organizationId?: string;
      limit?: number;
      suppressError?: boolean;
      background?: boolean;
      cursorUpdatedAt?: string;
      cursorId?: string;
      append?: boolean;
      noCache?: boolean;
    } = {}) {
      if (!background) self.isLoading = true;
      try {
        const headers = noCache ? { 'Cache-Control': 'no-cache' } : undefined;
        const doFetch = (params: {
          limit: number;
          document_type?: string;
          organization_id?: string;
          cursor_updated_at?: string;
          cursor_id?: string;
        }) => listOcrDocuments(params, headers);

        const res = yield doFetch({
          limit,
          document_type: documentType,
          organization_id: organizationId,
          cursor_updated_at: cursorUpdatedAt,
          cursor_id: cursorId,
        });

        const extractArray = (payload: unknown): unknown[] => {
          if (Array.isArray(payload)) return payload;
          const p = payload as {
            items?: unknown[];
            data?: unknown[];
            results?: unknown[];
            rows?: unknown[];
            docs?: unknown[];
          };
          if (Array.isArray(p?.items)) return p.items;
          if (Array.isArray(p?.data)) return p.data;
          if (Array.isArray(p?.results)) return p.results;
          if (Array.isArray(p?.rows)) return p.rows;
          if (Array.isArray(p?.docs)) return p.docs;
          if (payload && typeof payload === 'object') return Object.values(payload as Record<string, unknown>);
          return [];
        };

        type ListOcrDocumentsResponse = {
          data?: unknown;
          headers?: Record<string, string | undefined>;
        };

        const typedRes = res as ListOcrDocumentsResponse;
        let payload: unknown = typedRes?.data;
        let all: unknown[] = extractArray(payload);

        const filterDocs = (arr: unknown[]): unknown[] =>
          arr.filter((raw) => {
            const d = raw as Partial<OcrDocSnapshot>;
            const docType = String(d?.document_type ?? '').toLowerCase();
            const want = documentType ? String(documentType).toLowerCase() : '';
            const filename = String(d?.filename ?? '').toLowerCase();
            const typeOk = !want
              ? true
              : docType
                ? docType === want ||
                  docType.includes(want) ||
                  (want === 'audiobook' && (docType.includes('pdf') || docType.includes('book')))
                : want === 'audiobook'
                  ? filename.endsWith('.pdf')
                  : true;
            const orgOk = organizationId ? d?.organization_id === organizationId : true;
            return typeOk && orgOk;
          });

        let filtered = filterDocs(all);

        // Fallback: if filtered is empty on first page request and we used a filter, retry without document_type
        if (!cursorId && !cursorUpdatedAt && documentType && filtered.length === 0) {
          try {
            const retryRes = (yield doFetch({
              limit,
              organization_id: organizationId,
              cursor_updated_at: cursorUpdatedAt,
              cursor_id: cursorId,
            })) as ListOcrDocumentsResponse;
            payload = retryRes?.data;
            all = extractArray(payload);
            filtered = filterDocs(all);
          } catch (e) {
            // ignore fallback error; we'll return current state below
          }
        }

        const mapped: OcrDocSnapshot[] = filtered.map((raw) => {
          const d = raw as Partial<OcrDocSnapshot>;
          return {
            id: String(d.id ?? ''),
            filename: (d.filename as string) ?? '',
            size: (d.size as number) ?? null,
            document_type: (d.document_type as string) ?? null,
            created_at: (d.created_at as string) ?? null,
            updated_at: (d.updated_at as string) ?? null,
            page_count: (d.page_count as number) ?? null,
            organization_id: (d.organization_id as string) ?? null,
          } as OcrDocSnapshot;
        });
        const nextModels = mapped.map((snap) =>
          OcrDocumentModel.create({
            id: snap.id,
            filename: snap.filename,
            size: snap.size ?? null,
            document_type: snap.document_type ?? null,
            created_at: snap.created_at ?? null,
            updated_at: snap.updated_at ?? null,
            page_count: snap.page_count ?? null,
            organization_id: snap.organization_id ?? null,
          })
        );
        if (append) {
          const existingIds = new Set(self.items.map((d) => d.id));
          nextModels.forEach((m) => {
            if (!existingIds.has(m.id)) self.items.push(m);
          });
        } else {
          self.items.replace(nextModels);
        }
        // Persist lightweight cache for instant hydration
        try {
          sessionStorage.setItem('ocr_docs_cache', JSON.stringify(mapped.slice(0, 100)));
        } catch (_) {
          // ignore cache errors
        }
        const hdrs = typedRes.headers ?? {};
        self.nextCursorUpdatedAt = hdrs['x-next-cursor-updated-at'] ?? null;
        self.nextCursorId = hdrs['x-next-cursor-id'] ?? null;
        self.lastFetchedAt = Date.now();
        self.fetchedLimit = limit;
        return self.items.slice();
      } catch (err: unknown) {
        if (!suppressError) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status && status >= 500) {
            self.rootStore.errorStore.setError('api_not_ready_message');
          } else {
            const message = (err as Error)?.message ?? 'api_error';
            self.rootStore.errorStore.setError(message);
          }
        }
        return self.items.slice();
      } finally {
        if (!background) self.isLoading = false;
      }
    });

    const afterCreate = () => {
      hydrateFromCache();
    };

    const uploadPdf = flow(function* (
      file: File,
      params: { organization_id?: string; document_type?: 'audiobook' | 'course' } = {}
    ) {
      try {
        self.isUploading = true;
        if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
          throw new Error('Invalid file type');
        }
        if (file.size > OCR_MAX_PDF_BYTES) {
          throw new Error('File too large');
        }
        const document_type = params.document_type ?? 'audiobook';
        yield uploadOcrPdf(file, { document_type, organization_id: params.organization_id });
        try {
          yield list({ documentType: document_type, suppressError: true, background: true, noCache: true });
          self.rootStore.errorStore.setSuccess('save_success');
        } catch (e) {
          // handled in list()
        }
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      } finally {
        self.isUploading = false;
      }
    });

    const createFromOcr = flow(function* (
      ocrDocumentId: string,
      body: { language: string; title?: string; author?: string; organization_id?: string }
    ) {
      try {
        const res = yield createAudiobookFromOcr(ocrDocumentId, body);
        return res.data?.id as string;
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
        return null;
      }
    });

    const remove = flow(function* (id: string) {
      try {
        yield deleteOcrDocument(id);
        const idx = self.items.findIndex((d) => d.id === id);
        if (idx >= 0) self.items.splice(idx, 1);
        self.rootStore.errorStore.setSuccess('save_success');
        try {
          // Force a cache-bypass refresh once after delete
          yield list({ documentType: 'audiobook', limit: 50, noCache: true, background: true });
        } catch (e) {
          // ignore
        }
      } catch (err: unknown) {
        const message = (err as Error)?.message ?? 'api_error';
        self.rootStore.errorStore.setError(message);
      }
    });

    return { list, uploadPdf, createFromOcr, remove, afterCreate };
  });

export type OcrStoreInstance = typeof OcrStore.Type;
